import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import indexPriority from 'theme/z-index';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import loginApi from 'api/requests/login';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from 'context/user/User';
import fieldValidations from 'helpers/fieldValidations';
import paths from 'routes/paths';
import { X, Mail, AlertCircle, LogIn, ArrowRight, CheckCircle } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import OnboardingTags from '../sign-up/OnboardingTags';
import StepRegisterProvider from 'context/login/StepRegister';
import TagsProvider from 'context/events/Tags';
import userApi from 'api/requests/user';
import { trackEvent } from 'scripts/pixels';
import SignUpForm from './signup/SignUpForm';
import SignUpTagsSelect from './signup/SignUpTagsSelect';
import SignUpConfirmEmail from './signup/SignUpConfirmEmail';
import ForgotPasswordEmail from './forgot-password/ForgotPasswordEmail';
import ForgotPasswordCode from './forgot-password/ForgotPasswordCode';
import ForgotPasswordNewPassword from './forgot-password/ForgotPasswordNewPassword';

const VIEWS = {
  METHOD_SELECT: 'method_select',
  EMAIL_PASSWORD: 'email_password',
  LOGIN_CONFIRM_EMAIL: 'login_confirm_email',
  SIGN_UP_FORM: 'sign_up_form',
  SIGN_UP_TAGS: 'sign_up_tags',
  SIGN_UP_CONFIRM_EMAIL: 'sign_up_confirm_email',
  FORGOT_EMAIL: 'forgot_email',
  FORGOT_CODE: 'forgot_code',
  FORGOT_NEW_PASSWORD: 'forgot_new_password',
  FORGOT_SUCCESS: 'forgot_success',
};

export default function LoginModal({ isOpen, onClose }) {
  const { user, setUser, getUser } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  const errorTokens = {
    unconfirmed_email: 'unconfirmed_email',
    pending_onboarding: 'pending_onboarding',
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const [currentView, setCurrentView] = useState(VIEWS.METHOD_SELECT);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [buttonsLoading, setButtonsLoading] = useState(false);
  const [googleErrorMessage, setGoogleErrorMessage] = useState('');
  const [showPreferences, setShowPreferences] = useState(false);
  const [keepConnected, setKeepConnected] = useState(false);

  // Unconfirmed email login state
  const [unconfirmedCredentials, setUnconfirmedCredentials] = useState(null);

  // Sign-up state
  const [signUpData, setSignUpData] = useState(null);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState('');

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');

  const isSignUpView = [
    VIEWS.SIGN_UP_FORM,
    VIEWS.SIGN_UP_TAGS,
    VIEWS.SIGN_UP_CONFIRM_EMAIL,
  ].includes(currentView);

  const isLoginConfirmEmail = currentView === VIEWS.LOGIN_CONFIRM_EMAIL;

  const isForgotView = [
    VIEWS.FORGOT_EMAIL,
    VIEWS.FORGOT_CODE,
    VIEWS.FORGOT_NEW_PASSWORD,
    VIEWS.FORGOT_SUCCESS,
  ].includes(currentView);

  useEffect(() => {
    if (isOpen) {
      setCurrentView(VIEWS.METHOD_SELECT);
      setErrorMessage('');
      setErrorType('');
      setGoogleErrorMessage('');
      setSignUpData(null);
      setSignUpError('');
      setUnconfirmedCredentials(null);
      setForgotEmail('');
      setForgotCode('');
      reset();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    setCurrentView(VIEWS.METHOD_SELECT);
    setErrorMessage('');
    setGoogleErrorMessage('');
    setSignUpData(null);
    setSignUpError('');
    setUnconfirmedCredentials(null);
    setForgotEmail('');
    setForgotCode('');
    onClose();
  };

  const login = async ({ email, password }) => {
    try {
      setIsLoading(true);

      const { data } = await loginApi.signIn({
        email: email.toLowerCase(),
        password,
      });

      localStorage.setItem('token', data.accessToken);

      if (redirectTo) {
        searchParams.delete('redirectTo');
        navigate(
          {
            pathname: redirectTo,
            search: new URLSearchParams(Object.fromEntries(searchParams)).toString(),
          },
          { replace: true }
        );
      }
      window.location.reload();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setErrorType(error?.response?.data?.type);

      if (error?.response?.data?.type === errorTokens.unconfirmed_email) {
        const credentials = { email: email.toLowerCase(), password };
        setUnconfirmedCredentials(credentials);
        setErrorMessage('');
        setCurrentView(VIEWS.LOGIN_CONFIRM_EMAIL);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginEmailConfirmed = async ({ email, password }) => {
    setUnconfirmedCredentials(null);
    setCurrentView(VIEWS.METHOD_SELECT);
    await login({ email, password });
  };

  const handleTagsComplete = async (tagIds) => {
    try {
      await userApi.update(user.id, { tags: tagIds });
      if (redirectTo) {
        searchParams.delete('redirectTo');
        navigate(
          {
            pathname: redirectTo,
            search: new URLSearchParams(Object.fromEntries(searchParams)).toString(),
          },
          { replace: true }
        );
      }
      window.location.reload();
    } catch (error) {
      setGoogleErrorMessage(error?.response?.data?.message || 'Erro ao salvar preferências');
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) {
      setGoogleErrorMessage('Erro ao entrar com Google');
      return;
    }
    setGoogleErrorMessage('');
    setButtonsLoading(true);
    try {
      const { data } = await loginApi.withGoogle({
        credential: credentialResponse.credential,
      });
      localStorage.setItem('token', data.accessToken);

      if (data.isNewUser) {
        const fetchedUser = await getUser();
        if (!fetchedUser) {
          setGoogleErrorMessage('Erro ao carregar dados do usuário. Tente novamente.');
          setButtonsLoading(false);
          return;
        }
        setButtonsLoading(false);
        setShowPreferences(true);
        return;
      }

      if (redirectTo) {
        searchParams.delete('redirectTo');
        navigate(
          {
            pathname: redirectTo,
            search: new URLSearchParams(Object.fromEntries(searchParams)).toString(),
          },
          { replace: true }
        );
      }
      window.location.reload();
    } catch (error) {
      setGoogleErrorMessage(error?.response?.data?.message || 'Erro ao entrar com Google');
    } finally {
      setButtonsLoading(false);
    }
  };

  // ===== Sign-Up Handlers =====

  const handleSignUpFormNext = (userData) => {
    setSignUpData(userData);
    setSignUpError('');
    setCurrentView(VIEWS.SIGN_UP_TAGS);
  };

  const handleSignUpTagsNext = async (tagIds) => {
    if (!signUpData) return;

    setSignUpLoading(true);
    setSignUpError('');

    const payload = {
      ...signUpData,
      tags: tagIds,
    };

    try {
      await userApi.create(payload);

      // Este modal também abre a partir da página pública de um evento, e é
      // nesse contexto que a conta criada é um lead da campanha. Fora dele não
      // há pixel injetado e a chamada não faz nada.
      trackEvent('Lead');

      setCurrentView(VIEWS.SIGN_UP_CONFIRM_EMAIL);
    } catch (error) {
      setSignUpError(error?.response?.data?.message || 'Erro ao criar sua conta. Tente novamente.');
    } finally {
      setSignUpLoading(false);
    }
  };

  const handleSignUpEmailConfirmed = async ({ email, password }) => {
    await login({ email, password });
  };

  const startSignUp = () => {
    setErrorMessage('');
    setGoogleErrorMessage('');
    setCurrentView(VIEWS.SIGN_UP_FORM);
  };

  const backToLogin = () => {
    setSignUpData(null);
    setSignUpError('');
    setCurrentView(VIEWS.METHOD_SELECT);
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={showPreferences ? undefined : handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {showPreferences ? (
          <TagsProvider>
            <StepRegisterProvider>
              <OnboardingTags onComplete={handleTagsComplete} hideBack />
            </StepRegisterProvider>
          </TagsProvider>
        ) : (
          <>
            {/* ===== LOGIN CONFIRM EMAIL VIEW ===== */}
            {isLoginConfirmEmail ? (
              <>
                <ModalHeader>
                  <CloseButton onClick={handleClose} aria-label="Fechar modal">
                    <X size={20} color={colors.night} />
                  </CloseButton>
                </ModalHeader>

                <ModalBody>
                  <SignUpConfirmEmail
                    email={unconfirmedCredentials?.email}
                    password={unconfirmedCredentials?.password}
                    onConfirmed={handleLoginEmailConfirmed}
                  />
                </ModalBody>

                <ModalFooter>
                  <FooterText>
                    <SignUpLink onClick={backToLogin}>Voltar para o login</SignUpLink>
                  </FooterText>
                </ModalFooter>
              </>
            ) : isSignUpView ? (
              <>
                <ModalHeader>
                  <CloseButton onClick={handleClose} aria-label="Fechar modal">
                    <X size={20} color={colors.night} />
                  </CloseButton>
                </ModalHeader>

                <ModalBody>
                  {currentView === VIEWS.SIGN_UP_FORM && (
                    <SignUpForm
                      onNext={handleSignUpFormNext}
                      onBack={backToLogin}
                      initialData={signUpData}
                    />
                  )}

                  {currentView === VIEWS.SIGN_UP_TAGS && (
                    <TagsProvider>
                      <SignUpTagsSelect
                        onNext={handleSignUpTagsNext}
                        onBack={() => setCurrentView(VIEWS.SIGN_UP_FORM)}
                        isLoading={signUpLoading}
                        error={signUpError}
                      />
                    </TagsProvider>
                  )}

                  {currentView === VIEWS.SIGN_UP_CONFIRM_EMAIL && (
                    <SignUpConfirmEmail
                      email={signUpData?.email}
                      password={signUpData?.password}
                      onConfirmed={handleSignUpEmailConfirmed}
                    />
                  )}

                  <TermsText>
                    Ao criar sua conta, concordo com os{' '}
                    <TermsLink
                      onClick={() => {
                        handleClose();
                        navigate(paths.terms.useTerms);
                      }}
                    >
                      Termos de uso e políticas do Whizr
                    </TermsLink>
                  </TermsText>
                </ModalBody>

                <ModalFooter>
                  <FooterText>
                    Já possui uma conta?{' '}
                    <SignUpLink onClick={backToLogin}>Entrar</SignUpLink>
                  </FooterText>
                </ModalFooter>
              </>
            ) : isForgotView ? (
              <>
                {/* ===== FORGOT PASSWORD VIEWS ===== */}
                <ModalHeader>
                  <CloseButton onClick={handleClose} aria-label="Fechar modal">
                    <X size={20} color={colors.night} />
                  </CloseButton>
                </ModalHeader>

                <ModalBody>
                  {currentView === VIEWS.FORGOT_EMAIL && (
                    <ForgotPasswordEmail
                      onNext={(email) => {
                        setForgotEmail(email);
                        setCurrentView(VIEWS.FORGOT_CODE);
                      }}
                      onBack={backToLogin}
                    />
                  )}

                  {currentView === VIEWS.FORGOT_CODE && (
                    <ForgotPasswordCode
                      email={forgotEmail}
                      onNext={(code) => {
                        setForgotCode(code);
                        setCurrentView(VIEWS.FORGOT_NEW_PASSWORD);
                      }}
                      onBack={() => setCurrentView(VIEWS.FORGOT_EMAIL)}
                    />
                  )}

                  {currentView === VIEWS.FORGOT_NEW_PASSWORD && (
                    <ForgotPasswordNewPassword
                      email={forgotEmail}
                      code={forgotCode}
                      onSuccess={() => setCurrentView(VIEWS.FORGOT_SUCCESS)}
                      onBack={() => setCurrentView(VIEWS.FORGOT_CODE)}
                    />
                  )}

                  {currentView === VIEWS.FORGOT_SUCCESS && (
                    <ForgotSuccessView>
                      <SuccessIconCircle>
                        <CheckCircle size={28} color={colors.success} />
                      </SuccessIconCircle>
                      <ForgotSuccessTitle>Senha alterada!</ForgotSuccessTitle>
                      <ForgotSuccessText>
                        Sua senha foi alterada com sucesso. Agora você já pode entrar com sua nova senha.
                      </ForgotSuccessText>
                      <ForgotSuccessButton onClick={backToLogin}>
                        Voltar para o login
                      </ForgotSuccessButton>
                    </ForgotSuccessView>
                  )}
                </ModalBody>

                <ModalFooter>
                  <FooterText>
                    Lembrou sua senha?{' '}
                    <SignUpLink onClick={backToLogin}>Entrar</SignUpLink>
                  </FooterText>
                </ModalFooter>
              </>
            ) : (
              <>
                {/* ===== LOGIN VIEWS ===== */}
                <ModalHeader>
                  <CloseButton onClick={handleClose} aria-label="Fechar modal de login">
                    <X size={20} color={colors.night} />
                  </CloseButton>
                  <HeaderIconCircle>
                    <LogIn size={20} color={colors.occasionalPurple} />
                  </HeaderIconCircle>
                  <ModalTitle>Que bom ter você aqui!</ModalTitle>
                  <ModalSubtitle>Entre na sua conta para continuar</ModalSubtitle>
                </ModalHeader>

                <ModalBody>
                  {errorMessage && (
                    <ErrorBanner>
                      <AlertCircle size={18} color={colors.danger} />
                      <Text color={colors.danger} name="small" type="bold">
                        {errorMessage}
                      </Text>
                    </ErrorBanner>
                  )}

                  {googleErrorMessage && (
                    <ErrorBanner>
                      <AlertCircle size={16} color={colors.danger} />
                      <Text color={colors.danger} name="small">
                        {googleErrorMessage}
                      </Text>
                    </ErrorBanner>
                  )}

                  {currentView === VIEWS.METHOD_SELECT && (
                    <MethodSelectView>
                      <GoogleButtonContainer>
                        <GoogleButtonFace>
                          <GoogleIconSvg viewBox="0 0 24 24" width="20" height="20">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </GoogleIconSvg>
                          <span>Continuar com o Google</span>
                        </GoogleButtonFace>
                        <GoogleButtonOverlay>
                          <GoogleLogin
                            type="standard"
                            shape="rectangular"
                            size="large"
                            width={400}
                            onSuccess={handleGoogleSuccess}
                            onError={() => setGoogleErrorMessage('Erro ao entrar com Google')}
                          />
                        </GoogleButtonOverlay>
                      </GoogleButtonContainer>

                      <Divider>
                        <DividerLine />
                        <DividerText>ou</DividerText>
                        <DividerLine />
                      </Divider>

                      <MethodButton onClick={() => setCurrentView(VIEWS.EMAIL_PASSWORD)}>
                        <MethodIcon>
                          <Mail size={20} color={colors.occasionalPurple} />
                        </MethodIcon>
                        <span>Continuar com e-mail e senha</span>
                      </MethodButton>
                    </MethodSelectView>
                  )}

                  {currentView === VIEWS.EMAIL_PASSWORD && (
                    <EmailPasswordView>
                      <FormWrapper onSubmit={handleSubmit(login)}>
                        <FieldGroup>
                          <FieldLabel>
                            E-mail<Required>*</Required>
                          </FieldLabel>
                          <Fieldset
                            layout="squired"
                            placeholder="seu@email.com"
                            name="email"
                            register={register}
                            validations={fieldValidations.email()}
                          />
                          {errors.email?.message && (
                            <ErrorText>{errors.email?.message}</ErrorText>
                          )}
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>
                            Senha<Required>*</Required>
                          </FieldLabel>
                          <Fieldset
                            layout="squired"
                            badgeIconPassword
                            inputType="password"
                            badgeIconColorPassword={colors.night}
                            badgeIconPositionPassword="right"
                            name="password"
                            register={register}
                            validations={fieldValidations.passwordWithoutPatterns()}
                          />
                          {errors.password?.message && (
                            <ErrorText>{errors.password?.message}</ErrorText>
                          )}
                        </FieldGroup>

                        {!isLoading ? (
                          <SubmitButton
                            type="submit"
                            disabled={buttonsLoading}
                          >
                            <span>Entrar</span>
                            <ArrowRight size={18} />
                          </SubmitButton>
                        ) : (
                          <LoaderWrapper>
                            <Loader />
                          </LoaderWrapper>
                        )}
                      </FormWrapper>

                      <ForgotPasswordRow>
                        Esqueceu sua senha?{' '}
                        <ForgotPasswordLink
                          onClick={() => {
                            setErrorMessage('');
                            setCurrentView(VIEWS.FORGOT_EMAIL);
                          }}
                        >
                          Clique aqui
                        </ForgotPasswordLink>
                      </ForgotPasswordRow>
                    </EmailPasswordView>
                  )}

                  <TermsText>
                    Ao entrar, concordo com os{' '}
                    <TermsLink
                      onClick={() => {
                        handleClose();
                        navigate(paths.terms.useTerms);
                      }}
                    >
                      Termos de uso e políticas do Whizr
                    </TermsLink>
                  </TermsText>
                </ModalBody>

                <ModalFooter>
                  <FooterText>
                    Não possui uma conta?{' '}
                    <SignUpLink onClick={startSignUp}>Cadastre-se</SignUpLink>
                  </FooterText>
                </ModalFooter>

              </>
            )}
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
}

/* ===== Animations ===== */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* ===== Overlay ===== */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${indexPriority.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.2s ease-out;
`;

/* ===== Modal Container ===== */
const ModalContainer = styled.div`
  background: ${colors.neutralLight};
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${breakpoints.sm}) {
    max-width: 100%;
    max-height: 95vh;
  }
`;

/* ===== Header ===== */
const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 28px 0;
  position: relative;
`;

const HeaderIconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #F3EAFF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

/* ===== Body ===== */
const ModalBody = styled.div`
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
`;

/* ===== Method Selection View ===== */
const MethodSelectView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeIn} 0.2s ease-out;
`;

const MethodButton = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 20px;
  background: ${colors.neutralLight};
  border: 1.5px solid ${colors.neutralDark};
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: ${colors.nightDark};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.occasionalPurple}40;
    background: #FAFAFF;
  }

  &:active {
    transform: scale(0.99);
  }
`;

const MethodIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #F3EAFF;
  flex-shrink: 0;
`;

/* ===== Google Button Overlay ===== */
const GoogleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${({ compact }) => (compact ? '44px' : '48px')};
`;

const GoogleButtonFace = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: ${({ compact }) => (compact ? '10px' : '14px')};
  width: 100%;
  height: 100%;
  padding: ${({ compact }) => (compact ? '12px 16px' : '14px 20px')};
  background: ${colors.neutralLight};
  border: 1.5px solid ${colors.neutralDark};
  border-radius: 10px;
  font-size: ${({ compact }) => (compact ? '14px' : '15px')};
  font-weight: 500;
  color: ${colors.nightDark};
  transition: all 0.2s ease;
  pointer-events: none;

  ${GoogleButtonContainer}:hover & {
    border-color: ${colors.occasionalPurple}40;
    background: #FAFAFF;
  }
`;

const GoogleIconSvg = styled.svg`
  flex-shrink: 0;
`;

const GoogleButtonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  cursor: pointer;

  & > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    min-width: 100%;
    min-height: 100%;
  }

  & iframe {
    min-width: 400px !important;
    min-height: 48px !important;
  }
`;

/* ===== Email/Password View ===== */
const EmailPasswordView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.2s ease-out;
`;

/* ===== Form ===== */
const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.nightDark};
  letter-spacing: 0.01em;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: ${colors.danger};
`;

const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

/* ===== Submit Button ===== */
const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 4px auto 0;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(128, 37, 232, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;

/* ===== Forgot Password ===== */
const ForgotPasswordRow = styled.p`
  font-size: 13px;
  color: ${colors.night};
  text-align: center;
`;

const ForgotPasswordLink = styled.span`
  color: ${colors.occasionalPurple};
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

/* ===== Error Banner ===== */
const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #FEF2F2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
`;

/* ===== Terms ===== */
const TermsText = styled.p`
  font-size: 12px;
  color: ${colors.nightLight};
  text-align: center;
  margin-top: 20px;
  line-height: 1.5;
`;

const TermsLink = styled.span`
  color: ${colors.occasionalPurple};
  text-decoration: underline;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    opacity: 0.8;
  }
`;

/* ===== Footer ===== */
const ModalFooter = styled.div`
  padding: 16px 28px;
  border-top: 1px solid ${colors.neutralDark};
  text-align: center;
  background: ${colors.neutralBackground};
  border-radius: 0 0 12px 12px;
`;

const FooterText = styled.p`
  font-size: 14px;
  color: ${colors.night};
`;

const SignUpLink = styled.span`
  color: ${colors.occasionalPurple};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

/* ===== Divider ===== */
const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${colors.neutralDark};
`;

const DividerText = styled.span`
  font-size: 12px;
  color: ${colors.nightLight};
  font-weight: 500;
  text-transform: lowercase;
`;

/* ===== Forgot Password Success ===== */
const ForgotSuccessView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const SuccessIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${colors.success}15;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const ForgotSuccessTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const ForgotSuccessText = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 8px;
  margin-bottom: 24px;
  text-align: center;
  line-height: 1.5;
`;

const ForgotSuccessButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(128, 37, 232, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
`;
